import * as Progress from '@radix-ui/react-progress';
import type { HealthStatus } from '@/types/starter';
import styles from '@/styles/components/HealthIndicator.module.css';

interface HealthIndicatorProps {
  status: HealthStatus;
  daysSinceFeeding: number;
  onClick?: () => void;
}

const STATUS_CONFIG: Record<
  HealthStatus,
  {
    icon: string;
    label: string;
    actionText: string;
    progressPercent: number;
    containerClass: string;
    iconClass: string;
    labelClass: string;
    fillClass: string;
  }
> = {
  healthy: {
    icon: '✓',
    label: 'Healthy',
    actionText: 'Your starter is happy!',
    progressPercent: 100,
    containerClass: styles.healthy,
    iconClass: styles.iconHealthy,
    labelClass: styles.labelHealthy,
    fillClass: styles.fillHealthy,
  },
  'feed-today': {
    icon: '⚠',
    label: 'Feed Today',
    actionText: 'Time to feed your starter',
    progressPercent: 75,
    containerClass: styles.feedToday,
    iconClass: styles.iconFeedToday,
    labelClass: styles.labelFeedToday,
    fillClass: styles.fillFeedToday,
  },
  'feed-now': {
    icon: '⚠',
    label: 'Feed Now',
    actionText: 'At safe limit — feed immediately',
    progressPercent: 50,
    containerClass: styles.feedNow,
    iconClass: styles.iconFeedNow,
    labelClass: styles.labelFeedNow,
    fillClass: styles.fillFeedNow,
  },
  'needs-attention': {
    icon: '✕',
    label: 'Needs Attention',
    actionText: 'Starter may be weakened',
    progressPercent: 25,
    containerClass: styles.needsAttention,
    iconClass: styles.iconNeedsAttention,
    labelClass: styles.labelNeedsAttention,
    fillClass: styles.fillNeedsAttention,
  },
};

export function HealthIndicator({ status, daysSinceFeeding, onClick }: HealthIndicatorProps) {
  const config = STATUS_CONFIG[status];

  const subtextMessage =
    daysSinceFeeding === 0
      ? 'Fed today'
      : `Last fed ${daysSinceFeeding} day${daysSinceFeeding === 1 ? '' : 's'} ago${
          status === 'feed-now' ? ' — at safe limit' : ''
        }${status === 'needs-attention' ? ' — may be weakened' : ''}`;

  return (
    <div
      className={`${styles.container} ${config.containerClass}`}
      onClick={onClick}
      role="status"
      aria-label={`Starter status: ${config.label}. ${subtextMessage}`}
    >
      <div className={styles.statusRow}>
        <span className={`${styles.icon} ${config.iconClass}`} aria-hidden="true">
          {config.icon}
        </span>
        <div className={styles.textGroup}>
          <p className={`${styles.label} ${config.labelClass}`}>{config.label}</p>
          <p className={styles.subtext}>{subtextMessage}</p>
          <p className={styles.actionText}>{config.actionText}</p>
        </div>
      </div>

      <Progress.Root
        className={styles.progressBar}
        value={config.progressPercent}
        max={100}
        aria-label={`Starter health: ${config.progressPercent}%`}
      >
        <Progress.Indicator
          className={`${styles.progressFill} ${config.fillClass}`}
          style={{ width: `${config.progressPercent}%` }}
        />
      </Progress.Root>
    </div>
  );
}
